import type React from "react";
import { useState } from "react";

import { faCalendarCheck, faChevronDown, faClock } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import type { Form } from "contexts/CalendarContext";
import { useCalendarContext } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { DropDownButton } from "routes/patients/PatientProfile/components/PatientHeader/PatientHeaderInfo/components/DropdownButton";

export const AddButton: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();
  const { setActiveForm, setSelectedBookingInterval, setSelectedAppointment } = useCalendarContext();

  const [showFilters, setShowFilters] = useState(false);

  invariant(profile);

  const onClick = (form: Form) => {
    setSelectedBookingInterval(null);
    setSelectedAppointment(null);
    setActiveForm(form);

    navigate("/calendar");
  };

  const filters = [
    {
      children: (
        <Filter>
          <Icon>
            <StyledFontAwesomeIcon icon={faCalendarCheck} color={theme.colors.redesign.db50} />
          </Icon>
          {t("booking.forms.book_appointment")}
        </Filter>
      ),
      onClick: () => onClick("book_appointment"),
    },
    {
      children: (
        <Filter>
          <Icon>
            <StyledFontAwesomeIcon icon={faClock} color={theme.colors.redesign.db50} />
          </Icon>
          {t("booking.forms.availability")}
        </Filter>
      ),
      onClick: () => onClick("availability"),
    },
  ];

  return (
    <StyledDropdownButton
      id="calendar-filters"
      options={filters}
      border={false}
      onClick={() => setShowFilters(true)}
      onClose={() => setShowFilters(false)}
      $showFilters={showFilters}
      dropdownOrigin="right"
    >
      {t("booking.forms.header")} <StyledFontAwesomeIcon icon={faChevronDown} color={theme.colors.white} />
    </StyledDropdownButton>
  );
};

const StyledDropdownButton = styled(DropDownButton)<{ $showFilters: boolean }>`
  height: 40px;
  background: ${({ theme }) => theme.components.calendar.btn_bg};
  border: 1px solid ${({ theme }) => theme.components.calendar.btn_bg} !important;
  border-radius: 50px;

  #DropdownButton {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    padding: 12px 24px;
    color: ${({ theme }) => theme.colors.white};
    background: none;

    > svg {
      margin: 0 0 0 8px;
    }
  }
`;

const Filter = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  min-width: 16px;
  margin-right: 8px;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  height: 16px;
  width: 16px;
`;
